import { RecommendBidPrice } from 'core/rtbCampaign/RtbCampaign';
import { SuggestKeywords } from 'core/product/Product';
import { defaultTo, round, sumBy } from 'lodash';
import { hasCpcData } from 'core/pchome/KeywordBidCompetitiveness';

/**
 * 從已選關鍵字的成效列算出「建議出價區間」，語意與後端
 * `POST /pchome/suggestedKeywordsCpcBidRange` 完全相同：
 *
 *   bidCap   = max(各字 maxCpc)
 *   bidFloor = Σ(totalCpc) / Σ(cpcCounts)  ← daily CPC 的加權平均
 *
 * 後端的 `totalCpc` 沒有上 wire，但 `totalCpc = avgCpc × cpcCounts`，而 `avgCpc`
 * 就是 wire 上的 `bidPrice` ⇒ 用 `bidPrice × cpcCounts` 反推即可。反推誤差來自
 * `bidPrice` 被 round 到小數 2 位（≤ 0.005），加權後上界仍是 0.005 元，
 * 與關鍵字數量無關。
 *
 * `cpcCounts > 0`（走共用的 `hasCpcData`）是「這個字在後端的 CPC cache 裡有沒有資料」的
 * 判別式，不可省：
 * 後端算加權平均前會 `.filter(Objects::nonNull)` 把沒資料的字整個濾掉，而那些字
 * 上 wire 的 `bidPrice` / `maxCpc` 是 fallback 常數 20（不是真實資料）。少了這道
 * 過濾，一個沒有 CPC 資料的關鍵字就會把 `bidCap` 拉到 20，前後端顯示的區間會不一致。
 *
 * @returns 沒有任何一個字有 CPC 資料時回 `undefined`，對應後端的
 *          `if (aggregatedDTO.getCpcCounts() == 0) return defaultBidRange;`
 *          —— 交給呼叫端既有的 8/20 fallback，不可回 `{ bidFloor: 0, bidCap: 0 }`。
 */
export const computeRecommendBidPrice = (rows: SuggestKeywords[]): RecommendBidPrice | undefined => {
  const rowsWithCpcData = rows.filter(hasCpcData);
  const totalCpcCounts = sumBy(rowsWithCpcData, row => defaultTo(row.cpcCounts, 0));
  if (totalCpcCounts === 0) {
    return undefined;
  }
  const totalCpc = sumBy(
    rowsWithCpcData,
    row => defaultTo(row.bidPrice, 0) * defaultTo(row.cpcCounts, 0)
  );
  return {
    bidFloor: round(totalCpc / totalCpcCounts, 2),
    bidCap: round(Math.max(...rowsWithCpcData.map(row => defaultTo(row.maxCpc, 0))), 2)
  };
};
